import { useEffect, useRef, useState } from "react";
import { Box, styled } from "@mui/material";
import {
  KeyboardArrowDown as ChevronDownIcon,
  Check as CheckIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";

import { useUser } from "../hooks/user.js";
import { PORTAL } from "../theme/portals.js";
import { getEnv } from "../env.js";

const PORTAL_LABELS = {
  [PORTAL.KUNDENPORTAL]: "Kundenportal",
  [PORTAL.SCHADENPORTAL]: "Schadenportal",
};

const PORTAL_URL_KEYS = {
  [PORTAL.KUNDENPORTAL]: "VITE_KUNDENPORTAL_URL",
  [PORTAL.SCHADENPORTAL]: "VITE_SCHADENPORTAL_URL",
};

const PortalButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  background: "none",
  border: 0,
  padding: 0,
  cursor: "pointer",
  font: "inherit",
  fontSize: "1.125rem",
  fontWeight: 600,
  color: theme.palette.sidebarNav.text.active,
  "& .MuiSvgIcon-root": {
    color: theme.palette.sidebarNav.text.active,
  },
}));

const PortalName = styled(Box)(({ theme }) => ({
  fontSize: "1.125rem",
  fontWeight: 600,
  color: theme.palette.sidebarNav.text.active,
}));

// Anchors the portal menu to the switcher button, below it.
const PortalSwitch = styled(Box)({
  position: "relative",
});

const PortalMenu = styled("div")(({ theme }) => ({
  position: "absolute",
  top: "100%",
  left: 0,
  zIndex: 50,
  marginTop: theme.spacing(1),
  minWidth: "11rem",
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[4],
  overflow: "hidden",
}));

const PortalMenuEntry = styled("a", {
  shouldForwardProp: (prop) => prop !== "isCurrent",
})(({ theme, isCurrent }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(1),
  padding: theme.spacing(1.25, 2),
  textDecoration: "none",
  color: theme.palette.text.primary,
  fontSize: "0.875rem",
  fontWeight: isCurrent ? 600 : 400,
  "& svg": {
    fontSize: "1rem",
  },
  "&:not(:first-of-type)": {
    borderTop: `1px solid ${theme.palette.grey[100]}`,
  },
}));

/**
 * The current portal's name in the sidebar. For someone who may use the other
 * portal as well it opens a menu that links there; everyone else sees just the
 * name, with no mention of the other portal at all.
 *
 * @param {object} props
 * @param {string} props.current - the portal this app is (a PORTAL value)
 * @param {boolean} props.canSwitch - false shows just the name, regardless of
 *   the user's portals
 */
export default function PortalSwitcher({ current, canSwitch = true }) {
  const { user } = useUser();
  const [is_menu_open, setIsMenuOpen] = useState(false);
  const switch_area = useRef(null);

  const other = Object.values(PORTAL).find((portal) => portal !== current);
  const may_switch = canSwitch && (user?.portals ?? []).includes(other);

  useEffect(() => {
    if (!is_menu_open) {
      return undefined;
    }

    const closeOnOutsideClick = (event) => {
      if (!switch_area.current?.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("click", closeOnOutsideClick);
    return () => document.removeEventListener("click", closeOnOutsideClick);
  }, [is_menu_open]);

  if (!may_switch) {
    return <PortalName>{PORTAL_LABELS[current]}</PortalName>;
  }

  return (
    <PortalSwitch ref={switch_area}>
      <PortalButton
        type="button"
        aria-haspopup="menu"
        aria-expanded={is_menu_open}
        onClick={() => setIsMenuOpen((open) => !open)}
      >
        {PORTAL_LABELS[current]}
        <ChevronDownIcon fontSize="small" />
      </PortalButton>
      {is_menu_open && (
        <PortalMenu role="menu">
          <PortalMenuEntry role="menuitem" href="#" aria-current="true" isCurrent>
            {PORTAL_LABELS[current]}
            <CheckIcon fontSize="small" />
          </PortalMenuEntry>
          <PortalMenuEntry role="menuitem" href={getEnv(PORTAL_URL_KEYS[other])}>
            {PORTAL_LABELS[other]}
            <ChevronRightIcon fontSize="small" />
          </PortalMenuEntry>
        </PortalMenu>
      )}
    </PortalSwitch>
  );
}
